import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ImageModal from './ImageModal';
import ExportModal from './ExportModal';
import { bulkDeleteImages, bulkMoveImages } from '../../api/images';
import { getFolders } from '../../api/folders';

export default function ImageGrid({ images = [] }) {
  const qc = useQueryClient();
  const { data: folders = [] } = useQuery({ queryKey: ['folders'], queryFn: () => getFolders().then((r) => r.data) });
  const [preview, setPreview]       = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked]         = useState(() => new Set());
  const [exportImgs, setExportImgs] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [moving, setMoving]         = useState(false);

  if (!images.length) return null;

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clear     = () => setPicked(new Set());
  const selectAll = () => setPicked(new Set(images.map((i) => i.id)));

  function exitSelect() {
    setSelectMode(false);
    clear();
  }

  const pickedItems  = images.filter((i) => picked.has(i.id));
  const pickedImages = pickedItems.filter((i) => i.mime_type.startsWith('image/'));
  const openExport   = () => { if (pickedImages.length) setExportImgs(pickedImages); };

  async function deleteSelected() {
    if (!window.confirm(`Delete ${picked.size} selected item${picked.size !== 1 ? 's' : ''}? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await bulkDeleteImages([...picked]);
      await qc.invalidateQueries({ queryKey: ['images'] });
      exitSelect();
    } catch {
      /* keep selection so the user can retry */
    } finally {
      setDeleting(false);
    }
  }

  async function moveSelected(value) {
    if (value === '') return;
    setMoving(true);
    try {
      await bulkMoveImages([...picked], value === 'none' ? null : Number(value));
      await qc.invalidateQueries({ queryKey: ['images'] });
      await qc.invalidateQueries({ queryKey: ['folders'] });
      exitSelect();
    } catch {
      /* keep selection so the user can retry */
    } finally {
      setMoving(false);
    }
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        {!selectMode ? (
          <button onClick={() => setSelectMode(true)} className="btn-secondary text-sm">
            ☑ Select
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-300">Select items:</span>
            <button onClick={selectAll} className="text-sm text-brand-400 hover:underline">Select all</button>
            <button onClick={clear} className="text-sm text-gray-400 hover:underline">Clear</button>
            <button onClick={exitSelect} className="text-sm text-gray-400 hover:underline">Cancel</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {images.map((img) => (
          <ImageCard
            key={img.id}
            image={img}
            selectMode={selectMode}
            picked={picked.has(img.id)}
            onToggle={() => toggle(img.id)}
            onOpen={() => setPreview(img)}
          />
        ))}
      </div>

      {preview && <ImageModal image={preview} onClose={() => setPreview(null)} />}

      {/* Selection action bar */}
      {selectMode && picked.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-gray-900 border border-gray-700 shadow-xl rounded-full pl-4 pr-2 py-2 max-w-[95vw] flex-wrap justify-center">
          <span className="text-sm text-gray-200 whitespace-nowrap">{picked.size} selected</span>

          {/* Move to folder */}
          <select
            className="input text-sm py-1.5 rounded-full w-auto"
            value=""
            disabled={moving}
            onChange={(e) => moveSelected(e.target.value)}
            title="Move the selected items into a folder"
          >
            <option value="">{moving ? 'Moving…' : '📁 Move to…'}</option>
            <option value="none">— No folder (unfiled)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>

          <button
            onClick={openExport}
            disabled={!pickedImages.length}
            className="btn-primary text-sm px-4 py-1.5 rounded-full disabled:opacity-50 whitespace-nowrap"
            title={pickedImages.length ? 'Export the selected images as a PDF' : 'Select images to export as PDF'}
          >
            ↓ PDF{pickedImages.length && pickedImages.length !== picked.size ? ` (${pickedImages.length})` : ''}
          </button>

          <button
            onClick={deleteSelected}
            disabled={deleting}
            className="text-sm px-3 py-1.5 rounded-full bg-red-900/50 hover:bg-red-900/80 text-red-300 disabled:opacity-60 whitespace-nowrap"
          >
            {deleting ? 'Deleting…' : '🗑 Delete'}
          </button>
        </div>
      )}

      {exportImgs && (
        <ExportModal
          images={exportImgs}
          onClose={(done) => { setExportImgs(null); if (done) exitSelect(); }}
        />
      )}
    </>
  );
}

function PdfPlaceholder({ name }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 gap-1.5">
      <svg className="w-10 h-10 text-red-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
      </svg>
      <span className="text-[10px] font-bold text-red-400 tracking-widest">PDF</span>
      <span className="text-xs text-gray-400 truncate px-2 w-full text-center">{name}</span>
    </div>
  );
}

function EpubPlaceholder({ name }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 gap-1.5">
      <svg className="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-6 14H8v-2h4v2zm4-4H8v-2h8v2zm0-4H8V6h8v2z"/>
      </svg>
      <span className="text-[10px] font-bold text-blue-400 tracking-widest">EPUB</span>
      <span className="text-xs text-gray-400 truncate px-2 w-full text-center">{name}</span>
    </div>
  );
}

function ImageCard({ image, selectMode, picked, onToggle, onOpen }) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumbSrc    = `/uploads/thumbnails/${image.filename.replace(/\.[^.]+$/, '')}_thumb.webp`;
  const originalSrc = `/uploads/originals/${image.filename}`;
  const isPdf  = image.mime_type === 'application/pdf';
  const isEpub = image.mime_type === 'application/epub+zip';

  const handleClick = () => (selectMode ? onToggle() : onOpen());

  return (
    <div
      className={`group relative card overflow-hidden aspect-square cursor-pointer transition
        ${picked ? 'ring-2 ring-brand-500' : ''}`}
      onClick={handleClick}
    >
      {/* Thumbnail — fall back to placeholder if thumb missing */}
      {isPdf && thumbFailed ? (
        <PdfPlaceholder name={image.original_name} />
      ) : isEpub && thumbFailed ? (
        <EpubPlaceholder name={image.original_name} />
      ) : (
        <img
          src={thumbSrc}
          alt={image.original_name}
          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
          onError={(e) => {
            if (isPdf || isEpub) { setThumbFailed(true); }
            else { e.currentTarget.src = originalSrc; }
          }}
          loading="lazy"
        />
      )}

      {/* Select checkbox — shown for every item in select mode */}
      {selectMode && (
        <div
          className={`absolute top-1.5 left-1.5 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center text-sm font-bold leading-none shadow
            ${picked ? 'bg-brand-600 border-brand-400 text-white' : 'bg-black/60 border-white/80 text-transparent'}`}
        >
          ✓
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 pointer-events-none">
        <p className="text-white text-xs font-medium truncate w-full leading-snug">
          {image.original_name}
        </p>
      </div>

      {/* Share badge */}
      {image.share_token && (
        <div className="absolute top-1.5 right-1.5 bg-brand-600/80 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">
          🔗
        </div>
      )}

      {/* Category dot */}
      {image.category_color && (
        <div
          className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 rounded-full border border-black/20"
          style={{ background: image.category_color }}
          title={image.category_name}
        />
      )}
    </div>
  );
}
